/**
 * React Query hooks pour la messagerie moderne - Wennze
 * Hooks personnalisés utilisant React Query pour gérer les conversations et messages
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import config from '../config';
import modernMessagingService from '../services/ModernMessagingService';

const getMessageClientId = (message) => message?.client_id || message?.clientId || null;

const upsertMessageInInfiniteData = (old, incomingMessage) => {
  if (!incomingMessage) return old;

  if (!old) {
    return { pages: [{ results: [incomingMessage], next: null }], pageParams: [1] };
  }

  const incomingClientId = getMessageClientId(incomingMessage);
  const exists = old.pages.some((page) => (page.results || []).some((message) => {
    const sameId = message.id && incomingMessage.id && String(message.id) === String(incomingMessage.id);
    const sameClientId = incomingClientId && getMessageClientId(message) === incomingClientId;
    return sameId || sameClientId;
  }));

  const newPages = old.pages.map((page, pageIndex) => {
    const results = page.results || [];
    const updatedResults = results.map((message) => {
      const sameId = message.id && incomingMessage.id && String(message.id) === String(incomingMessage.id);
      const sameClientId = incomingClientId && getMessageClientId(message) === incomingClientId;

      if (sameId || sameClientId) {
        return { ...message, ...incomingMessage, _isOptimistic: false, status: 'sent' };
      }

      return message;
    });

    if (!exists && pageIndex === 0) {
      return { ...page, results: [incomingMessage, ...updatedResults] };
    }

    return { ...page, results: updatedResults };
  });

  return { ...old, pages: newPages };
};

/**
 * Hook pour récupérer la liste des conversations
 */
export const useConversations = (userId) => {
  return useQuery({
    queryKey: ['conversations', userId],
    queryFn: () => modernMessagingService.getConversations(),
    enabled: !!userId,
    staleTime: 30000, // 30 secondes
    cacheTime: 300000, // 5 minutes
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
};

/**
 * Hook pour récupérer les messages d'une conversation avec pagination infinie (OPTIMIZED)
 */
export const useMessages = (conversationId, recipientId, productId, userId) => {
  // Only enable the query if we have valid parameters
  const hasValidParams = conversationId || (recipientId && productId);
  
  // Debug logging with throttling to avoid spam
  React.useEffect(() => {
    if (hasValidParams) {

    }
  }, [conversationId, recipientId, productId, userId, hasValidParams]);
  
  return useInfiniteQuery({
    queryKey: ['messages', conversationId, recipientId, productId, userId],
    queryFn: async ({ pageParam = 1 }) => {
      const params = { 
        page: pageParam,
        page_size: 20,
        user_id: userId
      };
      
      if (conversationId) {
        params.conversation_id = conversationId;
      } else if (productId) {
        params.product_id = productId;
      }

      try {
        const result = await modernMessagingService.getMessages(params);

        return result;
      } catch (error) {
        console.error('useMessages queryFn error:', error);
        throw error;
      }
    },
    enabled: !!hasValidParams && !!userId,
    getNextPageParam: (lastPage) => {
      if (lastPage.next) {
        try {
          // Handle relative URLs like "?page=2&page_size=20"
          const nextUrl = lastPage.next.startsWith('?') 
            ? `http://localhost${lastPage.next}` 
            : lastPage.next;
          const url = new URL(nextUrl);
          const nextPage = parseInt(url.searchParams.get('page'));
          return nextPage;
        } catch (error) {
          console.error('Error parsing next page URL:', lastPage.next, error);
          // Fallback: calculate next page manually
          return (lastPage.page || 1) + 1;
        }
      }

      return undefined;
    },
    staleTime: 30000, // Cache for 30 seconds - WebSocket handles real-time updates
    cacheTime: 300000, // Keep in cache for 5 minutes after unmount
    retry: 1, // Reduce retries to avoid multiple calls
    refetchOnWindowFocus: false, // Don't refetch on focus - WebSocket handles real-time
    refetchOnMount: 'always', // Fetch on mount but use cache if fresh
    refetchOnReconnect: true, // Refetch on reconnect
    refetchInterval: false // Disable automatic refetching (WebSocket handles real-time)
  });
};

/**
 * Hook pour envoyer un message avec mise à jour optimiste
 */
export const useSendMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (messageData) => {
      // Handle audio messages with FormData
      if (messageData.formData && messageData.isAudio) {
        try {
          const response = await axios.post(
            `${config.backendUrl}/messaging/messages/`,
            messageData.formData,
            {
              headers: {
                ...modernMessagingService.getAuthHeaders(),
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          return response.data;
        } catch (error) {
          console.error('Audio message send error:', error);
          throw error;
        }
      } else {
        // Regular message handling
        return modernMessagingService.sendMessage(messageData);
      }
    },
    onMutate: async (messageData) => {
      // Générer un ID temporaire pour le message optimiste
      const tempId = `temp-${uuidv4()}`;
      const clientId = messageData.client_id || uuidv4();
      messageData.client_id = clientId;
      const optimisticMessage = {
        id: tempId,
        tempId,
        client_id: clientId,
        content: messageData.content,
        timestamp: new Date().toISOString(),
        sender: messageData.sender_id || messageData.sender,
        recipient: messageData.recipient,
        is_read: false,
        message_type: messageData.message_type || 'text',
        attachments: messageData.attachments || [],
        product: messageData.product_id || messageData.product ? { id: messageData.product_id || messageData.product } : null,
        _isOptimistic: true
      };

      // Annuler les requêtes en cours pour éviter les conflits
      await queryClient.cancelQueries({ queryKey: ['messages'] });

      // Sauvegarder les données actuelles
      const previousMessages = queryClient.getQueriesData({ queryKey: ['messages'] });

      // Mise à jour optimiste
      queryClient.getQueriesData({ queryKey: ['messages'] }).forEach(([queryKey]) => {
        queryClient.setQueryData(queryKey, (old) => upsertMessageInInfiniteData(old, optimisticMessage));
      });

      return { previousMessages, tempId };
    },
    onSuccess: (response) => {
      // Remplacer le message optimiste par le message réel
      const message = response?.message || response?.data || response;

      queryClient.getQueriesData({ queryKey: ['messages'] }).forEach(([queryKey]) => {
        queryClient.setQueryData(queryKey, (old) => upsertMessageInInfiniteData(old, message));
      });

      // Invalider et rafraîchir les conversations
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      // Also invalidate messages to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
    onError: (error, _messageData, context) => {
      // Restaurer les données précédentes en cas d'erreur
      if (context?.previousMessages) {
        context.previousMessages.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      
      console.error('Erreur lors de l\'envoi du message:', error);
    }
  });
};

/**
 * Hook pour marquer les messages comme lus
 */
export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, messageIds }) => 
      modernMessagingService.markAsRead(conversationId, messageIds),
    onSuccess: () => {
      // Invalider toutes les queries de messages et conversations
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    }
  });
};

/**
 * Hook pour supprimer un message
 */
export const useDeleteMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId) => modernMessagingService.deleteMessage(messageId),
    onMutate: async (messageId) => {
      // Mise à jour optimiste - retirer le message de toutes les conversations
      const queries = queryClient.getQueriesData({ queryKey: ['messages'] });
      
      queries.forEach(([queryKey, data]) => {
        if (data?.pages) {
          const newPages = data.pages.map(page => ({
            ...page,
            results: page.results.filter(msg => msg.id !== messageId)
          }));
          
          queryClient.setQueryData(queryKey, { ...data, pages: newPages });
        }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (error) => {
      console.error('Erreur lors de la suppression du message:', error);
      // Revalider toutes les queries en cas d'erreur
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    }
  });
};

/**
 * Hook pour la recherche de conversations
 */
export const useSearchConversations = (searchTerm) => {
  return useQuery({
    queryKey: ['conversations', 'search', searchTerm],
    queryFn: () => modernMessagingService.searchConversations(searchTerm),
    enabled: !!searchTerm && searchTerm.length >= 2,
    staleTime: 10000,
    cacheTime: 60000
  });
};

/**
 * Hook pour obtenir les statistiques non lues
 */
export const useUnreadStats = (userId) => {
  return useQuery({
    queryKey: ['unreadStats', userId],
    queryFn: () => modernMessagingService.getUnreadCount(),
    enabled: !!userId,
    staleTime: 5000,
    cacheTime: 30000,
    refetchInterval: 30000 // Actualiser toutes les 30 secondes
  });
};

/**
 * Hook pour synchroniser les messages en temps réel via WebSocket
 */
export const useRealtimeMessages = (conversationParams) => {
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (!conversationParams) return;

    const handleNewMessage = (message) => {
      queryClient.getQueriesData({ queryKey: ['messages'] }).forEach(([queryKey]) => {
        queryClient.setQueryData(queryKey, (old) => upsertMessageInInfiniteData(old, message));
      });

      // Invalider les conversations pour mettre à jour les aperçus
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    };

    const handleMessageUpdate = (data) => {
      // Handle regular message updates
      if (data.type === 'message_status') {
        const queries = queryClient.getQueriesData({ queryKey: ['messages'] });
        
        queries.forEach(([queryKey, cachedData]) => {
          if (cachedData?.pages) {
            const newPages = cachedData.pages.map(page => ({
              ...page,
              results: page.results.map(msg => 
                msg.id === data.messageId ? { ...msg, ...data.message } : msg
              )
            }));
            
            queryClient.setQueryData(queryKey, { ...cachedData, pages: newPages });
          }
        });
      }
      
      // Handle reaction updates
      if (data.type === 'reaction_update') {
        const queries = queryClient.getQueriesData({ queryKey: ['messages'] });
        
        queries.forEach(([queryKey, cachedData]) => {
          if (cachedData?.pages) {
            const newPages = cachedData.pages.map(page => ({
              ...page,
              results: page.results.map(msg => {
                if (msg.id === data.message_id) {
                  return {
                    ...msg,
                    reaction_summary: data.reaction_summary,
                    // Update user_reaction if it's the current user
                    user_reaction: data.action === 'added' && data.user_id === parseInt(conversationParams?.userId) 
                      ? data.emoji 
                      : data.action === 'removed' && data.user_id === parseInt(conversationParams?.userId)
                      ? null
                      : msg.user_reaction
                  };
                }
                return msg;
              })
            }));
            
            queryClient.setQueryData(queryKey, { ...cachedData, pages: newPages });
          }
        });
      }
    };

    // Connecter les événements WebSocket
    modernMessagingService.onNewMessage(handleNewMessage);
    modernMessagingService.onMessageUpdate(handleMessageUpdate);

    // Déconnecter les événements au démontage
    return () => {
      modernMessagingService.offNewMessage(handleNewMessage);
      modernMessagingService.offMessageUpdate(handleMessageUpdate);
    };
  }, [conversationParams, queryClient]);
};

/**
 * Hook pour modifier un message
 */
export const useEditMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messageId, newContent }) => 
      modernMessagingService.editMessage(messageId, newContent),
    onMutate: async ({ messageId, newContent }) => {
      // Annuler les requêtes en cours pour éviter les conflits
      await queryClient.cancelQueries({ queryKey: ['messages'] });

      // Sauvegarder l'état précédent
      const queryKey = ['messages'];
      const previousMessages = queryClient.getQueryData(queryKey);

      // Mise à jour optimiste
      queryClient.setQueryData(queryKey, (old) => {
        if (!old?.pages) return old;

        const newPages = old.pages.map(page => ({
          ...page,
          results: page.results.map((msg) => 
            msg.id === messageId 
              ? { 
                  ...msg, 
                  content: newContent,
                  is_edited: true,
                  edited_at: new Date().toISOString(),
                  editing: true // Temporary flag for UI
                }
              : msg
          )
        }));
        
        return { ...old, pages: newPages };
      });

      return { previousMessages, messageId, newContent };
    },
    onSuccess: (response, variables, context) => {
      // Mettre à jour avec les données du serveur
      const queryKey = ['messages'];
      queryClient.setQueryData(queryKey, (old) => {
        if (!old?.pages) return old;

        const newPages = old.pages.map(page => ({
          ...page,
          results: page.results.map((msg) => 
            msg.id === context.messageId 
              ? { ...response, editing: false }
              : msg
          )
        }));
        
        return { ...old, pages: newPages };
      });

      // Invalider les queries pour rafraîchir
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (error, variables, context) => {
      // Restaurer l'état précédent en cas d'erreur
      if (context?.previousMessages) {
        const queryKey = ['messages'];
        queryClient.setQueryData(queryKey, context.previousMessages);
      }
      
      console.error('Erreur lors de la modification du message:', error);
    }
  });
};

/**
 * Hook pour basculer une réaction sur un message
 */
export const useToggleReaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messageId, emoji }) => 
      modernMessagingService.toggleReaction(messageId, emoji),
    onMutate: async ({ messageId, emoji }) => {
      // Annuler les requêtes en cours pour tous les messages
      await queryClient.cancelQueries({ queryKey: ['messages'] });

      // Sauvegarder l'état précédent de toutes les queries messages
      const previousData = [];
      const queries = queryClient.getQueriesData({ queryKey: ['messages'] });
      
      queries.forEach(([queryKey, data]) => {
        previousData.push({ queryKey, data });
      });

      // Mise à jour optimiste sur toutes les queries messages
      queries.forEach(([queryKey, data]) => {
        if (data?.pages) {
          const newPages = data.pages.map(page => ({
            ...page,
            results: page.results.map((msg) => {
              if (msg.id === messageId) {
                const currentReactions = msg.reaction_summary || {};
                const newReactions = { ...currentReactions };
                
                // Check if user already reacted with this emoji
                const userAlreadyReacted = msg.user_reaction === emoji;
                
                if (userAlreadyReacted) {
                  // Remove reaction
                  if (newReactions[emoji] && newReactions[emoji] > 1) {
                    newReactions[emoji] -= 1;
                  } else {
                    delete newReactions[emoji];
                  }
                } else {
                  // Add reaction
                  newReactions[emoji] = (newReactions[emoji] || 0) + 1;
                }

                return {
                  ...msg,
                  reaction_summary: newReactions,
                  user_reaction: userAlreadyReacted ? null : emoji,
                  optimisticReaction: true // Flag for UI feedback
                };
              }
              return msg;
            })
          }));
          
          queryClient.setQueryData(queryKey, { ...data, pages: newPages });
        }
      });

      return { previousData };
    },
    onSuccess: (response, { messageId }) => {
      // Mettre à jour avec la réponse du serveur sur toutes les queries
      const queries = queryClient.getQueriesData({ queryKey: ['messages'] });
      
      queries.forEach(([queryKey, data]) => {
        if (data?.pages) {
          const newPages = data.pages.map(page => ({
            ...page,
            results: page.results.map((msg) => 
              msg.id === messageId 
                ? { 
                    ...msg, 
                    reaction_summary: response.reaction_summary,
                    user_reaction: response.user_reaction,
                    optimisticReaction: false
                  }
                : msg
            )
          }));
          
          queryClient.setQueryData(queryKey, { ...data, pages: newPages });
        }
      });
    },
    onError: (error, variables, context) => {
      // Restaurer l'état précédent en cas d'erreur
      if (context?.previousData) {
        context.previousData.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      console.error('Erreur lors de la gestion de la réaction:', error);
    }
  });
};

/**
 * Hook pour obtenir les réactions d'un message
 */
export const useMessageReactions = (messageId) => {
  return useQuery({
    queryKey: ['message-reactions', messageId],
    queryFn: () => modernMessagingService.getMessageReactions(messageId),
    enabled: !!messageId,
    staleTime: 30000, // Cache for 30 seconds
  });
};

/**
 * Hook pour obtenir un thread de messages
 */
export const useMessageThread = (messageId) => {
  return useQuery({
    queryKey: ['message-thread', messageId],
    queryFn: () => modernMessagingService.getMessageThread(messageId),
    enabled: !!messageId,
    staleTime: 30000, // Cache for 30 seconds
  });
};

/**
 * Hook pour répondre à un message
 */
export const useReplyToMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messageId, content }) => 
      modernMessagingService.replyToMessage(messageId, content),
    onMutate: async ({ messageId, content }) => {
      // Annuler les requêtes en cours
      await queryClient.cancelQueries({ queryKey: ['messages'] });

      // Sauvegarder l'état précédent
      const queryKey = ['messages'];
      const previousMessages = queryClient.getQueryData(queryKey);

      // Créer une réponse optimiste
      const optimisticReply = {
        id: `temp-${Date.now()}`,
        content,
        sender: JSON.parse(localStorage.getItem('user') || '{}').id,
        timestamp: new Date().toISOString(),
        reply_to: messageId,
        is_reply: true,
        _isOptimistic: true,
        status: 'sending'
      };

      // Mise à jour optimiste
      queryClient.setQueryData(queryKey, (old) => {
        if (!old?.pages) return old;

        const newPages = old.pages.map((page, index) => {
          if (index === 0) { // Add to first page
            return {
              ...page,
              results: [optimisticReply, ...page.results]
            };
          }
          return page;
        });

        return { ...old, pages: newPages };
      });

      return { previousMessages };
    },
    onSuccess: (response, { messageId }) => {
      // Remplacer le message optimiste par la réponse du serveur
      const queryKey = ['messages'];
      queryClient.setQueryData(queryKey, (old) => {
        if (!old?.pages) return old;

        const newPages = old.pages.map(page => ({
          ...page,
          results: page.results.map(msg => 
            msg._isOptimistic && msg.reply_to === messageId
              ? response.message
              : msg
          )
        }));

        return { ...old, pages: newPages };
      });

      // Invalider les queries de thread si ouvert
      queryClient.invalidateQueries({ queryKey: ['message-thread'] });
    },
    onError: (error, variables, context) => {
      // Restaurer l'état précédent en cas d'erreur
      if (context?.previousMessages) {
        const queryKey = ['messages'];
        queryClient.setQueryData(queryKey, context.previousMessages);
      }
      
      console.error('Erreur lors de la réponse:', error);
    }
  });
};
